import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldAlt, faRoute, faUserMd } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import ccmService from '../../../services/ccmService';
import {
  getCcmBillingPathLabel,
  getCcmComplexityMinuteTarget
} from '../../../utils/patientBillingUtils';

const ATTEST_ROLES = new Set([2, 3, 4]);

/**
 * CCM complexity attestation (provider) + live billing path (system-computed).
 */
export default function CcmComplexityAttestationPanel({
  patientId,
  practiceId,
  ccmBilling: ccmBillingProp,
  userRoleId,
  refreshKey = 0,
  onAttestationUpdated
}) {
  const [enrollment, setEnrollment] = useState(null);
  const [billingPreview, setBillingPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [pendingComplex, setPendingComplex] = useState(false);
  const [attestationNotes, setAttestationNotes] = useState('');
  const [confirmCarePlan, setConfirmCarePlan] = useState(false);
  const [confirmChronic, setConfirmChronic] = useState(false);

  const ccmBilling = ccmBillingProp ?? billingPreview;

  const canAttest = ATTEST_ROLES.has(Number(userRoleId));
  const isComplex = Boolean(enrollment?.is_complex ?? ccmBilling?.is_complex);
  const billingPath = ccmBilling?.billing_path || 'none';
  const suggestedCodes = (
    Array.isArray(ccmBilling?.suggested_codes)
      ? ccmBilling.suggested_codes
      : (ccmBilling?.eligible_cpt_codes || [])
  ).join(', ') || '—';
  const clinicalMinutes = ccmBilling?.clinical_staff_minutes ?? 0;
  const providerMinutes = ccmBilling?.provider_minutes ?? 0;
  const billingConflicts = ccmBilling?.billing_conflicts || [];
  const billingWarnings = ccmBilling?.billing_warnings || [];
  const minuteTarget = getCcmComplexityMinuteTarget(isComplex);

  const loadEnrollment = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const res = await ccmService.getEnrollmentByPatient(patientId);
      if (res?.success) {
        setEnrollment(res.data);
      }
    } catch (e) {
      setEnrollment(null);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  const loadBillingPreview = useCallback(async () => {
    if (!practiceId || !patientId || ccmBillingProp) return;
    try {
      const billingMonth = new Date().toISOString().substring(0, 7);
      const res = await ccmService.getQualifyingPatients(practiceId, billingMonth);
      if (!res?.success) {
        setBillingPreview(null);
        return;
      }
      const row = (res.data || []).find(
        (p) => String(p.patient_id) === String(patientId)
      );
      if (!row) {
        setBillingPreview(null);
        return;
      }
      setBillingPreview({
        is_complex: row.is_complex,
        billing_path: row.billing_path,
        clinical_staff_minutes: row.clinical_staff_minutes,
        provider_minutes: row.provider_minutes,
        billing_blocked: Boolean(row.billing_blocked),
        billing_conflicts: row.billing_conflicts || [],
        billing_warnings: row.billing_warnings || [],
        suggested_codes: Array.isArray(row.eligible_cpt_codes)
          ? row.eligible_cpt_codes
          : String(row.eligible_cpt_code || row.eligible_cpt_codes || '')
            .split(',')
            .map((c) => c.trim())
            .filter(Boolean)
      });
    } catch (e) {
      setBillingPreview(null);
    }
  }, [practiceId, patientId, ccmBillingProp]);

  useEffect(() => {
    loadEnrollment();
  }, [loadEnrollment]);

  useEffect(() => {
    loadBillingPreview();
  }, [loadBillingPreview, refreshKey]);

  const openAttestModal = (markComplex) => {
    setPendingComplex(markComplex);
    setAttestationNotes('');
    setConfirmCarePlan(false);
    setConfirmChronic(false);
    setShowModal(true);
  };

  const submitAttestation = async () => {
    const enrollmentId = enrollment?.enrollment_id;
    if (!enrollmentId) {
      toast.error('No active CCM enrollment found');
      return;
    }
    if (pendingComplex && (!confirmCarePlan || !confirmChronic)) {
      toast.error('Please confirm care plan review and chronic condition eligibility');
      return;
    }

    setSaving(true);
    try {
      const res = await ccmService.updateEnrollmentComplexity(enrollmentId, {
        is_complex: pendingComplex,
        attestation_notes: attestationNotes.trim() || undefined
      });
      if (res?.success) {
        toast.success(res.message || 'CCM complexity updated');
        setShowModal(false);
        await loadEnrollment();
        await loadBillingPreview();
        if (onAttestationUpdated) onAttestationUpdated();
      } else {
        toast.error(res?.message || 'Failed to update complexity');
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update complexity');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="pd-ccm-overview pd-ccm-complexity-panel">
        <p className="pd-ccm-complexity-muted">Loading CCM billing classification…</p>
      </div>
    );
  }

  if (!enrollment?.enrollment_id) {
    return null;
  }

  return (
    <>
      <div className="pd-ccm-overview pd-ccm-complexity-panel" style={{ marginTop: 24 }}>
        <div className="pd-ccm-overview-header">
          <h4 className="pd-ccm-overview-title">
            CCM Complexity & Billing Path
          </h4>
          <span className={`pd-ccm-badge ${isComplex ? 'pd-ccm-badge--complex' : 'pd-ccm-badge--active'}`}>
            {isComplex ? 'Complex Attested' : 'Non-Complex'}
          </span>
        </div>

        <div className="pd-ccm-complexity-grid">
          <div className="pd-ccm-complexity-card">
            <div className="pd-ccm-complexity-card-label">
              <FontAwesomeIcon icon={faUserMd} /> Provider attestation
            </div>
            <p className="pd-ccm-complexity-value">
              {isComplex
                ? 'Eligible for Complex CCM (99487 / 99489 when clinical staff reach 60+ min)'
                : 'Default Non-Complex CCM (99490 / 99491 family)'}
            </p>
            {enrollment.last_complexity_note ? (
              <p className="pd-ccm-complexity-muted">
                Last change{enrollment.attested_by_name ? ` by ${enrollment.attested_by_name}` : ''}
                {enrollment.last_complexity_change_date
                  ? ` on ${String(enrollment.last_complexity_change_date).substring(0, 10)}`
                  : ''}
                : {enrollment.last_complexity_note}
              </p>
            ) : (
              <p className="pd-ccm-complexity-muted">No complexity attestation on file yet.</p>
            )}
            {canAttest ? (
              <div className="pd-ccm-complexity-actions">
                {!isComplex ? (
                  <button type="button" className="cp-primary-btn" onClick={() => openAttestModal(true)}>
                    Attest Complex CCM
                  </button>
                ) : (
                  <button type="button" className="cp-cancel-btn" onClick={() => openAttestModal(false)}>
                    Revert to Non-Complex
                  </button>
                )}
              </div>
            ) : (
              <p className="pd-ccm-complexity-muted">
                {Number(userRoleId) === 1
                  ? 'Super Admin cannot change complexity attestation.'
                  : 'Only a provider or administrator can change attestation.'}
              </p>
            )}
          </div>

          <div className="pd-ccm-complexity-card pd-ccm-complexity-card--computed">
            <div className="pd-ccm-complexity-card-label">
              <FontAwesomeIcon icon={faRoute} /> Billing path this period (computed)
            </div>
            <p className="pd-ccm-complexity-value">{getCcmBillingPathLabel(billingPath)}</p>
            <ul className="pd-ccm-complexity-stats">
              <li>Clinical staff: <strong>{clinicalMinutes}</strong> min (target {minuteTarget} for {isComplex ? 'complex' : 'non-complex'})</li>
              <li>Provider personally: <strong>{providerMinutes}</strong> min</li>
              <li>Suggested CPTs: <strong>{suggestedCodes}</strong></li>
            </ul>
            {billingConflicts.length > 0 && (
              <div className="pd-ccm-complexity-blockers" role="alert">
                <p className="pd-ccm-complexity-blockers-title">Why billing is blocked</p>
                <ul>
                  {billingConflicts.map((c, i) => (
                    <li key={`${c.type || 'conflict'}-${i}`}>{c.message}</li>
                  ))}
                </ul>
              </div>
            )}
            {billingWarnings.length > 0 && (
              <div className="pd-ccm-complexity-warnings">
                <p className="pd-ccm-complexity-blockers-title">Warnings</p>
                <ul>
                  {billingWarnings.map((w, i) => (
                    <li key={`${w.type || 'warn'}-${i}`}>{w.message}</li>
                  ))}
                </ul>
              </div>
            )}
            <p className="pd-ccm-complexity-hint">
              Attestation sets eligibility. The billing path is recalculated from logged time each month — it is not chosen manually.
            </p>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="cp-modal-overlay" style={{ zIndex: 10001 }}>
          <div className="cp-modal-content" style={{ maxWidth: 520 }}>
            <div className="cp-builder-header">
              <h3 className="cp-builder-title" style={{ fontSize: 16 }}>
                {pendingComplex ? 'Attest Complex CCM' : 'Revert to Non-Complex CCM'}
              </h3>
              <button type="button" className="cp-cancel-btn" onClick={() => setShowModal(false)} disabled={saving}>
                Cancel
              </button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              {pendingComplex ? (
                <>
                  <p style={{ fontSize: 14, color: '#475569', marginBottom: 16 }}>
                    Complex CCM requires provider attestation that the patient&apos;s conditions and care plan
                    meet CMS complex CCM criteria. Billing uses <strong>99487 / 99489</strong> when clinical staff
                    log <strong>60+ minutes</strong> this month.
                  </p>
                  <label style={{ display: 'flex', gap: 8, marginBottom: 10, fontSize: 14 }}>
                    <input type="checkbox" checked={confirmChronic} onChange={(e) => setConfirmChronic(e.target.checked)} />
                    Patient has multiple significant chronic conditions supporting complex CCM
                  </label>
                  <label style={{ display: 'flex', gap: 8, marginBottom: 16, fontSize: 14 }}>
                    <input type="checkbox" checked={confirmCarePlan} onChange={(e) => setConfirmCarePlan(e.target.checked)} />
                    Comprehensive care plan reviewed and revised for complex needs
                  </label>
                </>
              ) : (
                <p style={{ fontSize: 14, color: '#475569', marginBottom: 16 }}>
                  This will mark the patient as <strong>Non-Complex CCM</strong>. Future billing will use
                  99490 / 99439 (clinical) or 99491 / 99437 (provider) paths based on logged time.
                </p>
              )}
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                Attestation notes (optional)
              </label>
              <textarea
                className="form-control"
                rows={3}
                value={attestationNotes}
                onChange={(e) => setAttestationNotes(e.target.value)}
                placeholder="Clinical rationale, care plan reference, etc."
                style={{ width: '100%', marginBottom: 16 }}
              />
              <button
                type="button"
                className="cp-primary-btn"
                onClick={submitAttestation}
                disabled={saving}
                style={{ width: '100%' }}
              >
                {saving ? 'Saving…' : pendingComplex ? 'Confirm Complex Attestation' : 'Confirm Revert to Non-Complex'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
